import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { FiArrowLeft, FiTrash2 } from 'react-icons/fi';
import { formatDateTime, TYPE_COLORS, STATUS_COLORS, PRIORITY_COLORS } from '../utils/techTask';
import TechTaskModal from '../components/TechTaskModal';

const TechTaskDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);

  const load = async () => {
    try {
      const res = await api.get(`/tech-tasks/${id}`);
      setTask(res.data.task);
      setComments(res.data.comments || []);
    } catch (e) {
      setTask(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const setStatus = async (status) => {
    const res = await api.patch(`/tech-tasks/${id}/status`, { status });
    setTask(res.data.task);
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    const res = await api.post(`/tech-tasks/${id}/comments`, { comment });
    setComments([...comments, res.data.comment]);
    setComment('');
  };

  const remove = async () => {
    if (!window.confirm('Delete this ticket?')) return;
    await api.delete(`/tech-tasks/${id}`);
    navigate('/tech/tasks');
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-400" />
      </div>
    );
  }

  if (!task) {
    return <p className="text-slate-400">Ticket not found.</p>;
  }

  return (
    <div className="max-w-2xl space-y-5 pb-10">
      <Link to="/tech/tasks" className="inline-flex items-center gap-2 text-slate-400 text-sm">
        <FiArrowLeft /> Tickets
      </Link>

      <div className="flex items-start justify-between gap-3">
        <h1 className="text-2xl font-bold text-white">{task.title}</h1>
        <div className="flex gap-2 shrink-0">
          <button type="button" onClick={() => setEditing(true)} className="text-xs px-3 py-2 rounded-lg bg-slate-800 text-slate-200">
            Edit
          </button>
          <button type="button" onClick={remove} className="p-2 rounded-lg text-red-400 bg-red-500/10">
            <FiTrash2 size={16} />
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <span className={`${STATUS_COLORS[task.status]} text-white text-xs px-2 py-1 rounded-full capitalize`}>
          {task.status.replace('_', ' ')}
        </span>
        <span className={`${TYPE_COLORS[task.task_type]} text-xs px-2 py-1 rounded-full capitalize`}>{task.task_type}</span>
        <span className={`${PRIORITY_COLORS[task.priority]} text-white text-xs px-2 py-1 rounded-full capitalize`}>
          {task.priority}
        </span>
      </div>

      {task.description && <p className="text-slate-300 text-sm leading-relaxed">{task.description}</p>}

      <div className="rounded-2xl bg-slate-900 border border-teal-500/20 p-4 space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-teal-400">Time & billing</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div>
            <p className="text-slate-500 text-xs">Start</p>
            <p className="text-white font-medium">{formatDateTime(task.start_at)}</p>
          </div>
          <div>
            <p className="text-slate-500 text-xs">End</p>
            <p className="text-white font-medium">{formatDateTime(task.end_at)}</p>
          </div>
        </div>
        {task.duration ? (
          <div className="pt-2 border-t border-slate-800">
            <p className="text-slate-500 text-xs">Time taken</p>
            <p className="text-xl font-bold text-teal-300">{task.duration.label}</p>
            <p className="text-sm text-slate-400 mt-0.5">{task.duration.totalHours} billable hours</p>
          </div>
        ) : (
          <p className="text-slate-500 text-sm">Set start and end (or mark completed) to calculate billable time.</p>
        )}
        {task.client_name && (
          <p className="text-sm text-slate-300">
            Client: <span className="text-white font-medium">{task.client_name}</span>
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="rounded-xl bg-slate-900 border border-slate-800 p-3">
          <p className="text-slate-500 text-xs">Assigned to</p>
          <p className="text-white">{task.assigned_to_name || '—'}</p>
        </div>
        <div className="rounded-xl bg-slate-900 border border-slate-800 p-3">
          <p className="text-slate-500 text-xs">Created by</p>
          <p className="text-white">{task.assigned_by_name || '—'}</p>
        </div>
      </div>

      <div>
        <p className="text-sm font-semibold text-white mb-2">Update status</p>
        <div className="grid grid-cols-2 gap-2">
          {['pending', 'in_progress', 'completed', 'blocked'].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              className={`py-3 rounded-xl text-sm capitalize touch-manipulation ${
                task.status === s ? 'bg-teal-500 text-slate-950 font-semibold' : 'bg-slate-900 text-slate-300 border border-slate-800'
              }`}
            >
              {s.replace('_', ' ')}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-slate-500 mt-2">
          In progress sets start time if empty. Completed sets end time if empty.
        </p>
      </div>

      <div>
        <p className="text-sm font-semibold text-white mb-2">Notes</p>
        <div className="space-y-2 mb-3">
          {comments.map((c) => (
            <div key={c.id} className="rounded-xl bg-slate-900 border border-slate-800 p-3">
              <p className="text-xs text-slate-500">
                {c.user_name} · {formatDateTime(c.created_at)}
              </p>
              <p className="text-sm text-slate-200 mt-1">{c.comment}</p>
            </div>
          ))}
        </div>
        <form onSubmit={addComment} className="flex gap-2">
          <input
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Add a note…"
            className="flex-1 px-3 py-3 rounded-xl bg-slate-900 border border-slate-800 text-white text-sm"
          />
          <button type="submit" className="px-4 py-3 rounded-xl bg-slate-800 text-teal-300 text-sm font-medium">
            Send
          </button>
        </form>
      </div>

      {editing && (
        <TechTaskModal
          task={task}
          onClose={() => setEditing(false)}
          onSuccess={() => {
            setEditing(false);
            load();
          }}
        />
      )}
    </div>
  );
};

export default TechTaskDetail;
